/**
 * TopicTemplatesTab — per-platform template picker for one ContentTopic.
 *
 * Per-platform multi-select picker that reuses `SystemTemplateBrowserDialog`
 * in 'pick' mode and the per-author `usePerAuthorPostTemplates` hook for
 * the data source.
 *
 * For each PlatformId row:
 *   - shows ALL currently-selected templates as a stack of compact cards;
 *     each card has its own delete affordance to remove a single id from
 *     the array
 *   - "Edit selection" / "Choose templates" button opens the picker dialog
 *     scoped to the project's default self-author + that platform, with
 *     the saved ids pre-checked
 *   - "Clear all" icon button when any are set → writes `deleteField()` for
 *     that platform key
 *
 * Storage contract: `topic.templateIdsByPlatform: Record<PlatformId, string[]>`
 * as the UI source of truth. Backend still reads `topic.defaultTemplateIds`
 * as a data compatibility fallback for older topic docs.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Box, Typography, Button, IconButton, Alert, Tooltip, Chip, Paper,
} from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { deleteField, doc, onSnapshot } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { SDataTable } from '@modules/ui-components';
import { useAuthor } from '../../hooks/useAuthor';
import { useAuthorPlatforms } from '../../hooks/useAuthorPlatforms';
import { usePerAuthorPostTemplates } from '../../hooks/usePerAuthorPostTemplates';
import { TopicBacklogFirestoreService } from '../../services/TopicBacklogFirestoreService';
import { SystemTemplateBrowserDialog } from '../SystemTemplateBrowserDialog';
import { PostTemplateEditDialog } from '../PostTemplateEditDialog';
import { buildPostTemplateColumns, withActionsColumn } from '../postTemplateColumns';
import type { ContentTopic, PlatformId } from '../../types';
import type { PostTemplate } from '@segmently/schema-models/content-plan';

export interface TopicTemplatesTabProps {
  projectId: string;
  topic: ContentTopic;
}

export const TopicTemplatesTab: React.FC<TopicTemplatesTabProps> = ({
  projectId, topic,
}) => {
  const { author, loading: authorLoading } = useAuthor(projectId);
  const { platforms: authorPlatforms } = useAuthorPlatforms(projectId, author?.id ?? null);
  const [pickerPlatform, setPickerPlatform] = useState<PlatformId | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<PostTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Parent (`ContentPlansPage`) holds `detailTopic` as a snapshot in
  // useState, so writes from this tab don't propagate back via the prop.
  // We subscribe directly to the live doc so the rendered selection stays
  // in sync after every Save / Remove. Falls back to the prop snapshot
  // until the first onSnapshot callback fires.
  const [liveTopic, setLiveTopic] = useState<ContentTopic | null>(null);
  useEffect(() => {
    if (!projectId || !topic.id) return;
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = doc(db, 'projects', projectId, 'topic_backlog', topic.id);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setLiveTopic({ ...(snap.data() as ContentTopic), id: snap.id });
      }
    });
    return () => unsub();
  }, [projectId, topic.id]);

  const effectiveTopic = liveTopic ?? topic;
  const templateIdsByPlatform = effectiveTopic.templateIdsByPlatform ?? {};
  const platformRows = useMemo(() => {
    const ids = new Set<string>();
    authorPlatforms
      .filter((platform) => platform.isActive !== false)
      .forEach((platform) => ids.add(platform.id));
    Object.keys(templateIdsByPlatform).forEach((platform) => ids.add(platform));
    return Array.from(ids).sort() as PlatformId[];
  }, [authorPlatforms, templateIdsByPlatform]);

  const persist = async (patch: Record<string, unknown>) => {
    setError(null);
    try {
      const service = new TopicBacklogFirestoreService();
      await service.update(projectId, topic.id, patch);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Replace the array for one platform with the new selection from the picker.
  const handleSaveSelection = async (platform: PlatformId, ids: string[]) => {
    if (ids.length === 0) {
      // Empty array semantically equals "no templates" — drop the key entirely.
      await persist({ [`templateIdsByPlatform.${platform}`]: deleteField() });
    } else {
      await persist({ [`templateIdsByPlatform.${platform}`]: ids });
    }
  };

  // Remove one id from an existing array. If the array becomes empty,
  // delete the key (`deleteField`) so the row collapses to "no templates".
  const handleRemoveOne = async (platform: PlatformId, id: string) => {
    const current = templateIdsByPlatform[platform] ?? [];
    const next = current.filter((x) => x !== id);
    if (next.length === 0) {
      await persist({ [`templateIdsByPlatform.${platform}`]: deleteField() });
    } else {
      await persist({ [`templateIdsByPlatform.${platform}`]: next });
    }
  };

  const handleClearPlatform = async (platform: PlatformId) => {
    await persist({ [`templateIdsByPlatform.${platform}`]: deleteField() });
  };

  if (authorLoading) {
    return (
      <Typography variant="caption" color="text.secondary" data-testid="topic-templates-tab">
        Loading…
      </Typography>
    );
  }

  if (!author) {
    return (
      <Alert severity="info" data-testid="topic-templates-tab">
        Set up the project's default author before picking per-platform templates.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} data-testid="topic-templates-tab">
      <Box>
        <Typography variant="subtitle2">Templates per platform</Typography>
        <Typography variant="caption" color="text.secondary">
          Pick one or more templates per platform from {author.displayName ?? 'your author'}'s
          library. The AI uses them as <code>formatHint</code> options when generating posts for
          this topic. Empty list = AI picks the format.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      )}

      <Stack spacing={1}>
        {platformRows.map((platform) => (
          <PlatformTemplateRow
            key={platform}
            projectId={projectId}
            authorId={author.id}
            platform={platform}
            currentIds={templateIdsByPlatform[platform] ?? []}
            onChoose={() => setPickerPlatform(platform)}
            onClearAll={() => handleClearPlatform(platform)}
            onRemoveOne={(id) => handleRemoveOne(platform, id)}
            onPreview={(template) => setPreviewTemplate(template)}
          />
        ))}
      </Stack>

      {pickerPlatform && (
        <PickerHost
          projectId={projectId}
          authorId={author.id}
          platform={pickerPlatform}
          currentIds={templateIdsByPlatform[pickerPlatform] ?? []}
          onClose={() => setPickerPlatform(null)}
          onPick={(ids) => {
            handleSaveSelection(pickerPlatform, ids);
          }}
        />
      )}

      {previewTemplate && (
        <PostTemplateEditDialog
          open
          onClose={() => setPreviewTemplate(null)}
          template={previewTemplate}
          mode="view"
          title={`Template — ${previewTemplate.name || previewTemplate.id}`}
        />
      )}
    </Stack>
  );
};

// ─── PlatformTemplateRow ─────────────────────────────────────────────

const PlatformTemplateRow: React.FC<{
  projectId: string;
  authorId: string;
  platform: PlatformId;
  currentIds: string[];
  onChoose: () => void;
  onClearAll: () => void;
  onRemoveOne: (id: string) => void;
  onPreview: (template: PostTemplate) => void;
}> = ({
  projectId, authorId, platform,
  currentIds, onChoose, onClearAll, onRemoveOne, onPreview,
}) => {
  // Subscribe to the author's templates for this platform — we use it to
  // render the saved selection's display payload (name, scaffold, etc.).
  const { templates, loading } = usePerAuthorPostTemplates(projectId, authorId, platform);

  // Resolved templates (only those whose id still exists in the author's
  // library) — fed straight to SDataTable. Orphan ids are surfaced as a
  // separate warning below the table so the user can clean them up.
  const resolvedTemplates = useMemo(
    () => currentIds
      .map((id) => templates.find((t) => t.id === id) ?? null)
      .filter((t): t is PostTemplate => t !== null),
    [templates, currentIds],
  );
  const orphanIds = useMemo(
    () => currentIds.filter((id) => !templates.find((t) => t.id === id)),
    [templates, currentIds],
  );

  // Same column defs the picker uses (Name / Goal / Scaffold / Tag), plus
  // a per-row remove-action column. No "Selected" / "Imported" badges
  // here — every row IS already selected by definition.
  const columns = useMemo(
    () => withActionsColumn(
      buildPostTemplateColumns(),
      (t) => (
        <Tooltip title="Remove this template">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveOne(t.id);
            }}
            data-testid={`topic-templates-remove-${platform}-${t.id}`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    ),
    [onRemoveOne, platform],
  );

  const hasSelection = currentIds.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5 }}
      data-testid={`topic-templates-row-${platform}`}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ minWidth: 96 }}>
            <Chip label={platform} size="small" />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {hasSelection
              ? `${currentIds.length} template${currentIds.length === 1 ? '' : 's'} selected`
              : 'No templates selected — AI will pick the format.'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant={hasSelection ? 'text' : 'outlined'}
              onClick={onChoose}
              data-testid={`topic-templates-choose-${platform}`}
            >
              {hasSelection ? 'Edit selection' : 'Choose templates'}
            </Button>
            {hasSelection && (
              <Tooltip title="Clear all templates for this platform">
                <IconButton
                  size="small"
                  onClick={onClearAll}
                  data-testid={`topic-templates-clear-${platform}`}
                >
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {hasSelection && (
          <Box>
            {/* Selected templates rendered via the same SDataTable + column
                defs that drive the picker (`buildPostTemplateColumns`).
                Row click → preview the template in PostTemplateEditDialog
                (view mode). Sorting + toolbars off — the list is short. */}
            <SDataTable<PostTemplate>
              columns={columns}
              data={resolvedTemplates}
              getRowId={(row) => row.id}
              enableSorting={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enablePagination={false}
              muiTableBodyRowProps={({ row }) => ({
                hover: true,
                onClick: () => onPreview(row.original),
                sx: { cursor: 'pointer' },
              })}
            />
            {orphanIds.length > 0 && !loading && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {orphanIds.length} saved template id{orphanIds.length === 1 ? '' : 's'}{' '}
                no longer exist in this author's library:{' '}
                {orphanIds.map((id, i) => (
                  <React.Fragment key={id}>
                    {i > 0 && ', '}
                    <code>{id}</code>{' '}
                    <IconButton
                      size="small"
                      onClick={() => onRemoveOne(id)}
                      data-testid={`topic-templates-remove-orphan-${platform}-${id}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </React.Fragment>
                ))}
              </Alert>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

// ─── PickerHost ──────────────────────────────────────────────────────
// Mounts SystemTemplateBrowserDialog in pick mode, feeding it the
// per-author per-platform templates as the external data source.

const PickerHost: React.FC<{
  projectId: string;
  authorId: string;
  platform: PlatformId;
  currentIds: string[];
  onClose: () => void;
  onPick: (ids: string[]) => void;
}> = ({ projectId, authorId, platform, currentIds, onClose, onPick }) => {
  const { templates, loading } = usePerAuthorPostTemplates(projectId, authorId, platform);

  return (
    <SystemTemplateBrowserDialog
      open
      onClose={onClose}
      mode="pick"
      platformPrefilter={platform}
      templates={templates}
      loading={loading}
      selectedIds={currentIds}
      onPick={onPick}
      title={`Pick templates — ${platform}`}
      subtitle={
        templates.length === 0 && !loading
          ? `No templates yet in this author's library for ${platform}. Open the platform's Templates section to import from the system catalogue or author one from scratch.`
          : `Tick all templates this topic should use on ${platform}, click a row to preview, then Save.`
      }
    />
  );
};

export default TopicTemplatesTab;
